import { useState, useRef } from "react";
import { Send } from "lucide-react";
import { theme } from "../../../theme";

type ChatComposerProps = {
  onSend: (content: string) => void;
  isRunning: boolean;
  isDisabled?: boolean;
  disabledMessage?: string;
};

const MAX_INPUT_LENGTH = 2000;

export const ChatComposer = ({
  onSend,
  isRunning,
  isDisabled = false,
  disabledMessage,
}: ChatComposerProps) => {
  const [input, setInput] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    const content = input.trim();
    if (!content || isRunning || isDisabled) return;
    onSend(content);
    setInput("");
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_INPUT_LENGTH) {
      setInput(value);
    }
    const textarea = e.target;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  return (
    <div className={theme.chat.inputBar}>
      <div className="flex items-center justify-center space-x-3">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask Charlie about concussion management..."
            maxLength={MAX_INPUT_LENGTH}
            rows={1}
            disabled={isDisabled}
            aria-label="Type your message to Charlie"
            aria-describedby="chat-disclaimer"
            className={`${theme.input.textarea} pr-12`}
          />
          <span
            className={`absolute right-3 bottom-2 text-xs ${
              input.length > MAX_INPUT_LENGTH * 0.9 ? "text-red-400" : theme.text.veryMuted
            }`}
          >
            {input.length}/{MAX_INPUT_LENGTH}
          </span>
        </div>
        <button
          onClick={handleSend}
          disabled={!input.trim() || isRunning || isDisabled}
          aria-label="Send message"
          className={theme.button.iconSend}
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
      {disabledMessage && (
        <p className="text-xs text-center py-1 text-amber-400">{disabledMessage}</p>
      )}
      <p id="chat-disclaimer" className={theme.chat.disclaimer}>
        Charlie is not a medical device. Always consult a medical professional for suspected
        concussions.
      </p>
    </div>
  );
};
